import type { Factor, Session } from '@getstep/proto/auth'
import type { MaskedAlias } from '@getstep/proto/common'
import { LoginFactorType, MaskedAliasType } from '@getstep/proto/common'
import { DataStore, DefaultAccessTokenProvider, UserStore } from '@getstep/sdk'
import { Api } from '@getstep/sdk/dist/http/Api'
import { PublicApi } from '@getstep/sdk/dist/http/PublicApi'
import { AuthStore } from '@getstep/sdk/dist/store/auth/AuthStore'
import { mustExist, notNullish } from '@getstep/sdk/dist/util/Assert'
import { DeviceStore } from '@lib/store/DeviceStore'
import { tokenStore } from '@lib/store/JwtTokenStore'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import { computed, makeObservable, observable } from 'mobx'

/**
 * The account recovery flow store. Invoked only for fully on-boarded users.
 */
export class RecoveryStore extends DataStore {
    static readonly className = 'RecoveryStore'

    session?: Session

    constructor(
        protected otpSessionId: string,
        protected userStore: UserStore,
        protected authStore: AuthStore,
    ) {
        super()

        makeObservable(this, {
            session: observable,
            maskedEmailAliases: computed,
            firstMaskedEmailAlias: computed,
            firstVerifiedMaskedEmailAlias: computed,
            hasVerifiedMaskedEmailAlias: computed,
        })
    }

    /**
     * Returns masked email aliases from session factors list.
     */
    get maskedEmailAliases(): MaskedAlias[] {
        const emailFactor = this.session?.factors.find(
            (factor) => factor.type === LoginFactorType.EMAIL,
        )
        return (emailFactor?.maskedAliases || []).filter(
            (alias) => alias.type === MaskedAliasType.MASKED_EMAIL,
        )
    }

    /**
     * Returns first not empty masked email alias from session factors list.
     */
    get firstMaskedEmailAlias(): MaskedAlias | undefined {
        return this.maskedEmailAliases[0]
    }

    /**
     * Returns first not empty verified masked email alias from session factors list.
     */
    get firstVerifiedMaskedEmailAlias(): MaskedAlias | undefined {
        return this.maskedEmailAliases.find((a) => a.isVerified)
    }

    get hasVerifiedMaskedEmailAlias(): boolean {
        return notNullish(this.firstVerifiedMaskedEmailAlias)
    }

    static async create(
        otpSessionId: string,
        sdk: SdkClientStore,
    ): Promise<RecoveryStore> {
        const refreshToken = mustExist(
            tokenStore.refreshToken,
            'refreshToken is required to create AuthorizedStore',
        )
        const apiClient = await sdk.createHttpClient()
        const publicApi = new PublicApi(apiClient)
        const tokenProvider = new DefaultAccessTokenProvider(publicApi, {
            refreshToken,
        })
        const api = new Api(
            apiClient,
            tokenStore,
            tokenStore,
            tokenProvider,
            undefined,
            undefined,
            DeviceStore.fingerprint,
        )
        const userStore = new UserStore(api)

        const authStore = new AuthStore(
            await sdk.createPublicApi(),
            tokenStore,
            tokenStore,
        )
        return new RecoveryStore(otpSessionId, userStore, authStore)
    }

    async useEmailBasedRecovery(): Promise<void> {
        console.debug('[RecoveryStore] use email-based recovery')
        const alias = mustExist(
            this.firstVerifiedMaskedEmailAlias,
            'alias must exist',
        )
        try {
            await mustExist(
                this.userStore,
                'sendEmailOtpForRecovery: UserStore must exist.',
            ).startMaskedAliasReVerify(alias)
        } catch (e: any) {
            throw e
        }
    }

    async enableEmail(code: string): Promise<void> {
        await this.authStore.continueOnboarding(this.otpSessionId, {
            password: code,
        })
    }

    /**
     * Returns login factor by it's type.
     *
     * @param type login factor type
     */
    getLoginFactorByType(type: LoginFactorType): Factor | undefined {
        return this.session?.factors.find((f) => f.type === type)
    }

    protected async performLoad(): Promise<void> {
        try {
            this.session = await this.userStore.getSession()
        } catch (e: any) {
            throw e
        }
    }
}
